import { DateTimeInfo } from "../../DateTimeInfo";
import { Nullable, Nullish, PartialNullable } from "../../Extensions";
import { StringHelper } from "../../Helpers/StringHelper";
import { IJNativeEvent, JNative, JNativeStatic } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property } from "../../RadObject";
import { IInputControl, InputControl, InputControlPropertyValues } from "./InputControl";
import { NAMESPACE } from "./_Namespace";

import "./Date.scss";

export type DatePropertyValues =
    InputControlPropertyValues
    & {
    };

export type DateProperties = {
    [name in keyof DatePropertyValues]: Property<DatePropertyValues[name]>
};

export interface IDate extends IInputControl, DateProperties
{
}

export class Date extends InputControl implements IDate
{
    public static override classNamespace = NAMESPACE + "Date";
    public static override className = "Date";

    // #region Global Variables

    protected $$valueElement?: JNative<HTMLInputElement>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<DatePropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-date ui input form");
    }

    // #endregion

    // #region Protected Methods

    protected _elementValueChanged()
    {
        if (!this.__processElementValueChanged)
            return;

        this.value.set(this._elementValueGet());
    }
    protected _elementValueGet(): Nullish<string>
    {
        return this.$$valueElement?.val();
    }
    protected _elementValueSet(value: Nullable<string>)
    {
        this.__processElementValueChanged = false;
        this.$$valueElement?.val(value);
        this.__processElementValueChanged = true;
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: DatePropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.text.overrideGet(next =>
        {
            const
                value = this.value.get();

            if (!value)
                return null;

            if (typeof value !== "string")
                return null;

            const
                dateResult = DateTimeInfo.tryFromDateString(value);

            return dateResult ? dateResult.toString("dd/MM/yyyy") : null;
        });
        this.value.overrideSet((value, next) =>
        {
            if (!value || !StringHelper.isDate(value, "yyyy-MM-dd"))
                value = null;

            this._elementValueSet(value);
            next(value);
        });
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        this.$$valueElement?.on("change", { self: this }, this.onValueElementChanged);
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement(),
            inputId = `${this.controlId}_input`;

        this.$$valueElement = JNativeStatic.create$$({
            nodeName: "input",
            attributes: {
                id: inputId,
                type: "date",
            }
        }) as JNative<HTMLInputElement>;

        this.$$valueElement.appendTo($$element);

        this.labelTargetId = inputId;

        return $$element;
    }
    protected override _enabledChanged(change: PropertyChange<boolean>)
    {
        super._enabledChanged(change);

        if (change.currentValue)
            this.$$valueElement?.removeAttr("disabled");
        else
            this.$$valueElement?.attr("disabled", "disabled");
    }

    // #endregion

    // #region Event Handlers

    protected onValueElementChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self._elementValueChanged();
    }

    // #endregion
}
